import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth.store";
import { authApi } from "@/features/auth/auth.api";
import { useT } from "@/i18n";
import { BugReportButton } from "@/components/BugReportButton";
import { ChatButton } from "@/features/chat/ChatPanel";
import { NotificationsBell } from "@/components/NotificationsBell";
import { PatientSearchBar } from "@/components/layout/PatientSearchBar";
import { LangToggle } from "@/components/chassis/LangToggle";
import { AgentHeader } from "@/components/layout/AgentHeader";

/**
 * App header — logo, primary nav, role badge, language, settings, logout.
 *
 * F5-R1a (feedback verbatim del CEO: «el header sigue siendo el mismo») —
 * re-vestido al brand F5: barra blanca, mark + wordmark Montserrat, pastillas
 * teal, toggle EN/ES del CHASIS (thumb gold — muere la copia local con
 * lavender). Misma estructura y funciones; solo el vestido.
 *
 * F5-R8a («Agente v2 — Header»): el rol AGENTE gana su propio header — el
 * chasis del navigator con pestañas Inicio · Mi cola · Mi agenda · Mis
 * números, búsqueda en la barra y chip de llamada abierta. Mueren, para el
 * agente, el chip de rol del medio y el nombre repetido. Los demás roles que
 * pasan por AppShell (/clients, /settings de admin/sup) conservan esta barra.
 */
export function Header(): JSX.Element {
  const t = useT();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();

  if (user?.role === "agent") return <AgentHeader />;

  const handleLogout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // ignore — still clear local
    } finally {
      clearSession();
      navigate("/login", { replace: true });
    }
  };

  const isAdminOrSup = user?.role === "admin" || user?.role === "supervisor";

  return (
    <header className="bg-white border-b border-nav-hair sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          <img src="/mark.png" alt="Pathway" className="h-[26px] w-auto" />
          <div className="leading-tight hidden sm:block">
            <p className="font-head text-xs font-extrabold tracking-[.14em] text-nav-navy uppercase">
              {t("brand.name")}
            </p>
            <p className="text-[11px] text-nav-slate">{t("brand.tagline")}</p>
          </div>
        </Link>

        {user && (
          <nav className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* F5-R9a: murió el NavLink legacy a /queue de la rama
                !isAdminOrSup — ningún agente ve esta barra (los agents
                reciben AgentHeader arriba) y para uploader/navigator era un
                link a una superficie ajena. La cola vive en el Inicio del
                agente. */}
            {isAdminOrSup && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  "inline-flex items-center gap-1.5 px-3 min-h-[44px] rounded-[10px] font-head text-sm font-semibold transition-colors " +
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal " +
                  (isActive
                    ? "bg-nav-tealTint text-nav-tealDark"
                    : "text-nav-slate hover:bg-nav-soft hover:text-nav-navy")
                }
              >
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">{t("header.admin")}</span>
              </NavLink>
            )}
          </nav>
        )}

        {user && user.role !== "uploader" && (
          <div className="flex-1 max-w-md min-w-[140px]">
            <PatientSearchBar />
          </div>
        )}

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {user && (
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-nav-slate truncate max-w-[14rem]">{user.fullName}</span>
              <RoleBadge role={user.role} />
            </div>
          )}
          {user && <NotificationsBell />}
          <ChatButton />
          <BugReportButton />
          <LangToggle />
          {user && (
            <Link
              to="/settings"
              className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-[10px] text-nav-slate hover:bg-nav-soft hover:text-nav-navy transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              aria-label={t("header.settings")}
              title={t("header.settings")}
            >
              <Settings className="h-4 w-4" />
            </Link>
          )}
          {user && (
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("header.signOut")}</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

function RoleBadge({ role }: { role: string }): JSX.Element {
  const t = useT();
  // F5-R1a — tonos del brand: admin/supervisor en teal (interactivo/autoridad),
  // agent en sage (positivo). Sin lavender/sky/mint.
  const map: Record<string, string> = {
    admin: "bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]",
    supervisor: "bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]",
    agent: "bg-nav-sageTint text-nav-sageText border-nav-sage",
  };
  const cls = map[role] ?? "bg-nav-hair text-nav-slate border-nav-card";
  const labelKey = `header.role.${role}` as const;
  // Fallback to raw role if key isn't in our map
  const label = (labelKey === "header.role.admin" || labelKey === "header.role.supervisor" || labelKey === "header.role.agent")
    ? t(labelKey)
    : role;
  return (
    <span
      className={"inline-flex items-center px-2 py-0.5 rounded-full font-head text-[11px] font-bold border " + cls}
    >
      {label}
    </span>
  );
}
